import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Ticket,
  Loader2,
  Gift,
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  Megaphone,
  RefreshCw,
  DollarSign,
  BarChart3,
  PieChart,
  Clock,
  AlertTriangle,
  ArrowUpRight,
  Percent,
  Activity,
} from "lucide-react";

const CODE_TYPES = {
  single_use: { label: "Usage unique", icon: Ticket, color: "bg-blue-500" },
  multi_uses_limit: { label: "Multi-usages", icon: Users, color: "bg-purple-500" },
  multi_uses_expiry: { label: "Avec expiration", icon: Calendar, color: "bg-amber-500" },
  influencer: { label: "Influenceur", icon: Megaphone, color: "bg-pink-500" },
};

const PromoAnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30d");

  const periodToDays = (p) => ({ "7d": 7, "30d": 30, "90d": 90, "all": 365 }[p] || 30);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, conversionsRes] = await Promise.all([
        axios.get(`${API}/admin/promo-codes/analytics/dashboard?period=${period}`),
        axios.get(`${API}/admin/promo-codes/conversions?days=${periodToDays(period)}`),
      ]);
      setAnalytics(analyticsRes.data);
      setConversions(conversionsRes.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Erreur lors du chargement des analytics");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-CA", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("fr-CA", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-20">
        <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">Impossible de charger les analytics</p>
        <Button onClick={fetchAnalytics} variant="outline" className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Réessayer
        </Button>
      </div>
    );
  }

  const { summary, top_codes, performance_by_type, daily_activity, recent_usages, expiring_soon } = analytics;

  // Calculate max for chart scaling
  const maxActivity = Math.max(
    ...daily_activity.map((d) => Math.max(d.created, d.used)),
    1
  );

  return (
    <div className="space-y-6">
      {/* Header with period selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Rapports & Analytics</h2>
          <p className="text-sm text-gray-500">
            Généré le {formatDateTime(analytics.generated_at)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">7 derniers jours</SelectItem>
              <SelectItem value="30d">30 derniers jours</SelectItem>
              <SelectItem value="90d">90 derniers jours</SelectItem>
              <SelectItem value="all">Tout le temps</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAnalytics}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Total Codes</p>
                <p className="text-2xl font-bold text-gray-900">{summary.total_codes}</p>
              </div>
              <div className="p-2 bg-brand-100 rounded-lg">
                <Gift className="h-5 w-5 text-brand-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Assignés</p>
                <p className="text-2xl font-bold text-purple-600">{summary.total_assigned}</p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Utilisés</p>
                <p className="text-2xl font-bold text-green-600">{summary.total_used}</p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Conversion</p>
                <p className="text-2xl font-bold text-blue-600">{summary.conversion_rate}%</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Percent className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Économies</p>
                <p className="text-2xl font-bold text-amber-600">${summary.total_savings_given.toFixed(2)}</p>
              </div>
              <div className="p-2 bg-amber-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Transactions</p>
                <p className="text-2xl font-bold text-gray-900">{summary.transactions_with_promo}</p>
              </div>
              <div className="p-2 bg-gray-100 rounded-lg">
                <Activity className="h-5 w-5 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Conversions par code (ROI) */}
      {conversions && (
        <Card data-testid="promo-conversions-section">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <ArrowUpRight className="h-5 w-5 text-emerald-600" />
                  Conversions par code
                </CardTitle>
                <CardDescription>
                  Performance de chaque code sur les {conversions.period_days} derniers jours
                </CardDescription>
              </div>
              <div className="flex gap-6 text-right">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Utilisations</p>
                  <p className="text-xl font-bold text-emerald-600" data-testid="conv-total-uses">
                    {conversions.totals.total_uses}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Clients uniques</p>
                  <p className="text-xl font-bold text-blue-600" data-testid="conv-distinct-users">
                    {conversions.totals.total_distinct_users}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Moy./jour</p>
                  <p className="text-xl font-bold text-purple-600">
                    {conversions.totals.avg_uses_per_day}
                  </p>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {conversions.per_code.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                Aucune utilisation de code promo dans cette période
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Type / Cible</TableHead>
                      <TableHead>Campagne</TableHead>
                      <TableHead className="text-center">Utilisations</TableHead>
                      <TableHead className="text-center">Clients uniques</TableHead>
                      <TableHead>Dernière utilisation</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {conversions.per_code.map((c) => {
                      const typeInfo = CODE_TYPES[c.code_type] || CODE_TYPES.single_use;
                      const TypeIcon = typeInfo.icon;
                      return (
                        <TableRow key={c.id} data-testid={`conv-row-${c.code}`}>
                          <TableCell>
                            <div className="flex flex-col gap-0.5">
                              <code className="font-mono font-bold text-brand-600">{c.code}</code>
                              <span className="text-xs text-gray-500 truncate max-w-[200px]" title={c.description}>
                                {c.description}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <Badge className={`${typeInfo.color} text-white text-xs w-fit`}>
                                <TypeIcon className="h-3 w-3 mr-1" />
                                {typeInfo.label}
                              </Badge>
                              {c.applies_to === "first_transaction_only" && (
                                <Badge className="bg-pink-100 text-pink-700 text-xs w-fit">
                                  <Gift className="h-3 w-3 mr-1" />
                                  1ère transaction
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {c.campaign_name || <span className="text-gray-400">—</span>}
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="font-bold text-emerald-600 text-lg">{c.uses_in_period}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="font-semibold text-blue-600">{c.distinct_users_count}</span>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {formatDateTime(c.last_used_at)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity Chart */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-brand-600" />
              Activité quotidienne
            </CardTitle>
            <CardDescription>Codes créés vs utilisés</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-48 flex items-end gap-1">
              {daily_activity.map((day, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex gap-0.5 items-end h-36">
                    {/* Created bar */}
                    <div
                      className="flex-1 bg-brand-200 rounded-t transition-all hover:bg-brand-300"
                      style={{ height: `${(day.created / maxActivity) * 100}%`, minHeight: day.created > 0 ? "4px" : "0" }}
                      title={`Créés: ${day.created}`}
                    />
                    {/* Used bar */}
                    <div
                      className="flex-1 bg-green-400 rounded-t transition-all hover:bg-green-500"
                      style={{ height: `${(day.used / maxActivity) * 100}%`, minHeight: day.used > 0 ? "4px" : "0" }}
                      title={`Utilisés: ${day.used}`}
                    />
                  </div>
                  <span className="text-[10px] text-gray-400 truncate w-full text-center">
                    {idx % 3 === 0 ? day.day_label : ""}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-6 mt-3 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-brand-200 rounded" />
                <span className="text-gray-600">Créés</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-400 rounded" />
                <span className="text-gray-600">Utilisés</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Performance by Type */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <PieChart className="h-5 w-5 text-brand-600" />
              Performance par type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(CODE_TYPES).map(([type, info]) => {
                const perf = performance_by_type[type] || { count: 0, total_used: 0, conversion_rate: 0 };
                const Icon = info.icon;
                return (
                  <div key={type} className="flex items-center gap-3">
                    <div className={`p-1.5 rounded ${info.color} text-white`}>
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium truncate">{info.label}</span>
                        <span className="text-sm text-gray-500">{perf.count} codes</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${info.color} transition-all`}
                            style={{ width: `${Math.min(perf.conversion_rate, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 w-10 text-right">
                          {perf.conversion_rate}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Codes */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Top 10 codes les plus utilisés
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_codes.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Aucun code utilisé pour le moment</p>
            ) : (
              <div className="space-y-2">
                {top_codes.map((code, idx) => (
                  <div
                    key={code.code}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50"
                  >
                    <span className="w-6 h-6 flex items-center justify-center text-xs font-bold text-white bg-brand-600 rounded-full">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <code className="font-mono font-semibold text-brand-600 text-sm">
                          {code.code}
                        </code>
                        <Badge variant="secondary" className="text-xs">
                          {code.discount_rate}%
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 truncate">{code.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-green-600">{code.times_used}</p>
                      <p className="text-xs text-gray-400">utilisations</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Usages */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              Utilisations récentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recent_usages.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Aucune utilisation récente</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {recent_usages.map((usage, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 border-l-2 border-green-400"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                          {usage.personal_code || usage.code}
                        </code>
                        <ArrowUpRight className="h-3 w-3 text-green-500" />
                      </div>
                      <p className="text-xs text-gray-600 truncate mt-0.5">
                        {usage.user_name || usage.user_email || "Utilisateur"}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">{formatDateTime(usage.used_at)}</p>
                      <p className="text-xs font-medium text-green-600">-{usage.discount_rate}%</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Expiring Soon Alert */}
      {expiring_soon.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Codes expirant bientôt ({expiring_soon.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {expiring_soon.map((code) => (
                <div
                  key={code.code}
                  className="flex items-center justify-between p-2 bg-white rounded-lg border border-amber-200"
                >
                  <div>
                    <code className="font-mono font-semibold text-sm">{code.code}</code>
                    <p className="text-xs text-gray-500">{code.times_used} utilisations</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-medium text-amber-600">
                      Expire le {formatDate(code.expires_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PromoAnalyticsDashboard;
